<template>
  <div class="borrowerAttributeCurrency">
    <BorrowerAttributeConstructor
      :questionText="questionText"
      :description="description"
      :descriptionTooltip="descriptionTooltip"
      :label="null"
      :helpText="helpText"
      :disclaimer="disclaimer">
      <template v-slot:input>
        <div class="form-floating">
          <input
            v-if="!disabled"
            :key="identifier"
            :id="'ba' + identifier"
            :name="'ba' + identifier"
            :data-cy="question.alias"
            :dreams-id="question.alias"
            inputmode="numeric"
            :maxlength="15"
            type="text"
            class="form-control"
            :class="{
              'is-invalid': v$.value && v$.value.$invalid && v$.value.$dirty || errorState
            }"
            separator=","
            :pattern="question.pattern"
            :placeholder="label"
            :value="mask"
            :disabled="disabled || question.group === 'bankValues'"
            :required="question.required"
            v-maska:[options]
            data-maska="0.99"
            data-maska-tokens="0:\d:multiple|9:\d:optional"
            @blur="onBlur(v$)"
            @focus="onFocus()"
            @input="setValue($event)"
            @paste="setValue($event)"
            @keydown.enter="setValue($event, true)"
          />
          <input v-else :value="mask"
            :class="{
              'disabled': disabled,
              'disabled-repressed': true && disabled && repressDisabledStyle,
            }"
            class="form-control" disabled />
          <label :for="'ba' + identifier"><span>{{ label }}</span></label>
          <small class="invalid-feedback" v-if="v$.value && v$.value.$error">
            <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon> {{ errorText }}
          </small>
          <client-only>
            <LendioBootstrapTooltip v-if="tooltipText">
              <span class="text-tooltip-icon" data-bs-toggle="tooltip" data-bs-placement="right" :title="tooltipText">
                <fa-icon :icon="['fal', 'circle-info']"></fa-icon>
              </span>
            </LendioBootstrapTooltip>
          </client-only>
        </div>
      </template>
    </BorrowerAttributeConstructor>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { mapState } from 'pinia'
import get from 'lodash/get'
import { currency, unformattedNumber } from '~/libs/format'
import BaseBorrowerAttributeMixin from './BaseBorrowerAttributeMixin'
import { useCobrandStore } from '~/store/cobrand'

  export default {
    props: {
      disabled: Boolean,
      repressDisabledStyle: Boolean,
      errorState: Boolean,
    },
    emits: ['invalidity', 'answer'],
    setup: () => ({ v$: useVuelidate() }),
    data () {
      return {
        errorMessage: 'Please enter a valid dollar amount',
        placeholderMessage: '',
        focused: false,
      }
    },
    async mounted () {
      this.$watch('invalid', (invalid) => {
        this.$emit('invalidity', invalid)
      }, {immediate: true})
    },
    computed: {
      ...mapState(useCobrandStore, ['config']),
      invalid() {
        return this.v$.$invalid
      },
      errorText() {
        return get(this.v$, 'value.$error') ? this.errorMessage : ''
      },
      mask() {
        return this.value ? currency(this.value, false, '$') : null
      },
      options() {
       const options = {
          preProcess: val => val.replace(/[$,]/g, ''),
          postProcess: val => {
            if (!val) return ''

            const sub = 3 - (val.includes('.') ? val.length - val.indexOf('.') : 0)

            return Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD'
            }).format(val)
              .slice(0, sub ? -sub : undefined)
          }
        }
        return options
      }
    },
    methods: {
      // trigger new state
      onFocus() {
        this.focused = true
      },
      onBlur (v$) {
        this.focused = false
        // Trigger validation if user entered and exited input without a value.
        v$.$touch()
      },
      setValue (e, submitImmediately = false) {
        // Same as onBlur, but ensures validation happens if user enters a value
        // then deletes it before blurring. Shows error sooner.
        this.v$.value.$touch()
        this.value = unformattedNumber(e.target.value)
        this.$emit('answer', { value: this.value, isInvalid: this.v$.value.$error, submitImmediately, isTrusted: e.isTrusted })
      },
      customValidation(value) {
        if (this.question.customValidation) {
          const result = this.question.customValidation(value)
          this.errorMessage = result.errorMessage
          return result.valid
        }
        switch (this.question.alias) {
          default:
            return true;
          }
      },
      restrictKeys (event) {
        let allowedKeys = [8, 9, 13, 16, 37, 39, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105]
        if (!allowedKeys.includes(event.keyCode)) {
          event.preventDefault()
        }
      }
    },
    mixins: [BaseBorrowerAttributeMixin],
    validations() {
      let validations
      if (this.question.alias === 'financeAmount') {
        validations = {
          value: {
            required,
            pattern: (val) => {
              return val > 0
            }
          }
        }
      } else {
        validations = {
          value: {
            required: requiredIf(() => !this.question.mustAllowNull),
            pattern: (val) => {
              return this.customValidation(val)
            }
          }
        }
      }

      return {
        ...validations,
      }
    }
  }
</script>

<style lang='scss' scoped>
label {
  color: $type-400;
}
.text-tooltip-icon {
  position: absolute;
  top: 1rem;
  right: 1rem;
  color: $blue-300;
}

/* No border radius between $ prefix and input element. */
.input-group .form-floating:not(:first-child) > input {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>
